import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { Plus, Trash2, Upload, Building2 } from "lucide-react";
import { Pagination, usePagination } from "@/components/Pagination";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const inputCls = "w-full px-3 py-2 bg-white border border-[#EAE7E0] text-sm text-[#2A2421] focus:border-[#C88270] focus:outline-none";
const labelCls = "block text-[10px] uppercase tracking-[0.2em] text-[#5C5652] mb-1.5";

export default function QuoteReferences({ onChanged }) {
  const confirm = useConfirm();
  const [refs, setRefs] = useState([]);
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [uploading, setUploading] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/quote-references"); setRefs(data); onChanged?.(data); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (!name.trim()) { toast.error("Reference name is required"); return; }
    try { await api.post("/quote-references", { name: name.trim(), company: company.trim() }); setName(""); setCompany(""); await load(); toast.success("Reference added"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const save = async (r) => { try { await api.put(`/quote-references/${r.id}`, { name: r.name, company: r.company }); } catch (err) { toast.error(formatApiError(err)); } };
  const remove = async (r) => {
    const ok = await confirm({ title: "Delete reference?", description: `Delete "${r.name}"? Quotes linked to it will lose the reference.`, confirmText: "Delete", destructive: true });
    if (!ok) return;
    try { await api.delete(`/quote-references/${r.id}`); await load(); toast.success("Deleted"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const uploadLogo = async (rid, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(rid);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/quote-references/${rid}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } }); await load(); toast.success("Logo uploaded"); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(null); e.target.value = ""; }
  };
  const setField = (rid, patch) => setRefs((prev) => prev.map((r) => (r.id === rid ? { ...r, ...patch } : r)));

  const { page, setPage, totalPages, pageItems, total, pageSize } = usePagination(refs, 8, refs.length);

  return (
    <div className="space-y-6" data-testid="quote-references">
      <div className="bg-white border border-[#EAE7E0] p-6 space-y-4">
        <div className="font-serif text-xl text-[#2A2421]">Add Reference Partner</div>
        <p className="text-xs text-[#5C5652]">Create partners you can co-brand quotations with. Available to pick when creating a Partner quotation.</p>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div><label className={labelCls}>Name *</label><input value={name} onChange={(e) => setName(e.target.value)} data-testid="ref-new-name" placeholder="Planner / Contact name" className={inputCls} /></div>
          <div><label className={labelCls}>Company</label><input value={company} onChange={(e) => setCompany(e.target.value)} data-testid="ref-new-company" placeholder="Company / Brand" className={inputCls} /></div>
          <button onClick={add} data-testid="ref-add-btn" className="inline-flex items-center justify-center gap-2 px-6 py-2.5 text-xs uppercase tracking-[0.2em] bg-[#2A2421] text-white hover:bg-[#C88270] transition-colors"><Plus size={14} /> Add</button>
        </div>
      </div>

      {refs.length === 0 ? (
        <div className="p-12 bg-white border border-[#EAE7E0] text-center text-[#5C5652]" data-testid="no-references">No reference partners yet.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {pageItems.map((r) => (
            <div key={r.id} className="bg-white border border-[#EAE7E0] p-5 flex gap-4" data-testid={`ref-card-${r.id}`}>
              <div className="shrink-0">
                <div className="w-20 h-20 bg-[#FAF9F6] border border-[#EAE7E0] flex items-center justify-center overflow-hidden">
                  {r.logo ? (
                    <img src={`${BACKEND_URL}/api/public/portfolio/${r.logo.id}`} alt={r.name} className="w-full h-full object-contain" data-testid={`ref-logo-${r.id}`} />
                  ) : (
                    <Building2 size={22} className="text-[#B8B2AA]" />
                  )}
                </div>
                <label className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-[#C88270] hover:text-[#2A2421] cursor-pointer" data-testid={`ref-logo-upload-${r.id}`}>
                  <Upload size={12} /> {uploading === r.id ? "…" : "Logo"}
                  <input type="file" accept="image/*" onChange={(e) => uploadLogo(r.id, e)} className="hidden" />
                </label>
              </div>
              <div className="flex-1 space-y-2">
                <div><label className={labelCls}>Name</label><input value={r.name} onChange={(e) => setField(r.id, { name: e.target.value })} onBlur={() => save(r)} data-testid={`ref-name-${r.id}`} className={inputCls} /></div>
                <div><label className={labelCls}>Company</label><input value={r.company || ""} onChange={(e) => setField(r.id, { company: e.target.value })} onBlur={() => save(r)} data-testid={`ref-company-${r.id}`} className={inputCls} /></div>
                <button onClick={() => remove(r)} data-testid={`ref-delete-${r.id}`} className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-[#5C5652] hover:text-red-600"><Trash2 size={13} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {total > 0 && <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onChange={setPage} testId="references-pagination" />}
    </div>
  );
}
